import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
  collection, query, where, orderBy, onSnapshot,
  doc, getDoc, setDoc, serverTimestamp,
} from 'firebase/firestore';
import { signOut } from 'firebase/auth';

function formatDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

function formatTime(t) {
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

export default function Home() {
  const navigate  = useNavigate();
  const name      = localStorage.getItem('ericgcuts_name') || 'there';
  const phone     = auth.currentUser?.phoneNumber || '';
  const rawDigits = (phone || '').replace(/\D/g, '');
  // clientId = last 10 digits (strip leading 1 if 11 digits)
  const clientId  = rawDigits.length === 11 ? rawDigits.slice(1) : rawDigits;

  const [upcoming,    setUpcoming]    = useState([]);
  const [optIn,       setOptIn]       = useState(false);
  const [optInLoaded, setOptInLoaded] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    const today = todayStr();
    const q = query(
      collection(db, 'appointments'),
      where('clientPhone', '==', rawDigits.slice(-10)),
      where('date', '>=', today),
      orderBy('date'), orderBy('time')
    );
    const unsub = onSnapshot(q, snap => {
      setUpcoming(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [clientId, rawDigits]);

  // Load opt-in preference
  useEffect(() => {
    if (!clientId) return;
    getDoc(doc(db, 'clients', clientId)).then(snap => {
      if (snap.exists()) {
        setOptIn(snap.data().optInCancellations === true);
      }
      setOptInLoaded(true);
    }).catch(() => setOptInLoaded(true));
  }, [clientId]);

  async function toggleOptIn() {
    const newVal = !optIn;
    setOptIn(newVal);
    try {
      await setDoc(doc(db, 'clients', clientId), {
        optInCancellations: newVal,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (e) {
      setOptIn(!newVal); // revert on error
    }
  }

  const STATUS_COLOR = {
    scheduled: 'var(--accent)',
    completed: 'var(--success)',
    cancelled: 'var(--danger)',
    pending:   'var(--subtext)',
  };

  return (
    <div style={{ padding: 24, paddingTop: 48, paddingBottom: 80 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
        <div>
          <div style={{ fontSize: 13, color: 'var(--subtext)', marginBottom: 4 }}>Welcome back</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: 'var(--text)' }}>Hey, {name} 👋</div>
        </div>
        <button
          onClick={() => { signOut(auth); localStorage.clear(); }}
          style={{ background: 'none', color: 'var(--subtext)', fontSize: 13, padding: 8, border: 'none', cursor: 'pointer' }}
        >
          Sign out
        </button>
      </div>

      {/* Book CTA */}
      <button
        onClick={() => navigate('/book')}
        style={{
          width: '100%', background: 'var(--accent)', borderRadius: 18,
          padding: '20px 24px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', marginBottom: 32,
          border: 'none', cursor: 'pointer',
        }}
      >
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: 18, fontWeight: 900, color: '#000' }}>Book a Cut ✂️</div>
          <div style={{ fontSize: 13, color: 'rgba(0,0,0,0.6)', marginTop: 2 }}>See available times</div>
        </div>
        <span style={{ fontSize: 28, color: '#000' }}>→</span>
      </button>

      {/* Upcoming appointments */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--subtext)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
          Your Appointments
        </div>

        {upcoming.length === 0 ? (
          <div style={{
            background: 'var(--card)', borderRadius: 16, border: '1px solid var(--border)',
            padding: 32, textAlign: 'center',
          }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>📅</div>
            <div style={{ color: 'var(--subtext)', fontSize: 15 }}>No upcoming appointments</div>
            <button
              onClick={() => navigate('/book')}
              style={{
                marginTop: 16, background: 'var(--accent)', borderRadius: 12,
                padding: '12px 24px', fontSize: 15, fontWeight: 800, color: '#000',
                border: 'none', cursor: 'pointer',
              }}
            >
              Book Now
            </button>
          </div>
        ) : (
          upcoming.map(a => (
            <div key={a.id} style={{
              background: 'var(--card)', borderRadius: 16, border: '1px solid var(--border)',
              padding: 18, marginBottom: 10, display: 'flex', gap: 14,
            }}>
              <div style={{
                width: 4, borderRadius: 4, flexShrink: 0,
                background: STATUS_COLOR[a.status] || 'var(--accent)',
              }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{a.serviceName}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--accent)' }}>${Number(a.price).toFixed(0)}</div>
                </div>
                <div style={{ fontSize: 13, color: 'var(--subtext)', marginBottom: 6 }}>{formatDate(a.date)}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, color: 'var(--subtext)' }}>{formatTime(a.time)} · {a.duration} min</div>
                  <div style={{
                    fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                    background: (STATUS_COLOR[a.status] || 'var(--accent)') + '22',
                    color: STATUS_COLOR[a.status] || 'var(--accent)',
                    textTransform: 'capitalize',
                  }}>
                    {a.status}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Message Eric */}
      <button
        onClick={() => navigate('/messages')}
        style={{
          width: '100%', background: 'var(--card)', borderRadius: 16,
          border: '1px solid var(--border)', padding: '18px 20px',
          display: 'flex', alignItems: 'center', gap: 14,
          cursor: 'pointer', marginBottom: 20,
        }}
      >
        <span style={{ fontSize: 28 }}>💬</span>
        <div style={{ textAlign: 'left', flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Message Eric</div>
          <div style={{ fontSize: 13, color: 'var(--subtext)', marginTop: 2 }}>Ask about availability, styles, etc.</div>
        </div>
        <span style={{ color: 'var(--subtext)', fontSize: 20 }}>›</span>
      </button>

      {/* Notification opt-in */}
      {optInLoaded && (
        <div style={{
          background: 'var(--card)', borderRadius: 16, border: '1px solid var(--border)',
          padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <span style={{ fontSize: 24 }}>🔔</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
              Cancellation Alerts
            </div>
            <div style={{ fontSize: 13, color: 'var(--subtext)', marginTop: 2, lineHeight: 1.4 }}>
              Get notified when Eric has a last-minute opening
            </div>
          </div>
          {/* Toggle switch */}
          <div
            onClick={toggleOptIn}
            style={{
              width: 50, height: 28, borderRadius: 14,
              background: optIn ? 'var(--accent)' : 'var(--border)',
              position: 'relative', cursor: 'pointer',
              transition: 'background 0.2s',
              flexShrink: 0,
            }}
          >
            <div style={{
              position: 'absolute',
              top: 3, left: optIn ? 25 : 3,
              width: 22, height: 22, borderRadius: 11,
              background: '#fff',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }} />
          </div>
        </div>
      )}
    </div>
  );
}
